import React, { useCallback, useEffect, useState } from 'react';
import { compact } from 'lodash';
import styled, { css, useTheme } from 'styled-components';
import { AutosizeInput } from 'venn-components';
import { GetColor, Subtitle2, StudioButton, Icon } from 'venn-ui-kit';
import { useHasFF } from 'venn-utils';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface InputRowProps {
  name: string;
  onNameChange: (name: string) => void;
  onEdit: () => void;
  onDelete?: () => void;
  hoverForOnlyName?: boolean;
  readonly?: boolean;
  noHoverStyles?: boolean;
  children: React.ReactNode;
  testid?: string;
  className?: string;
  labelFor?: string;
}

export const InputRow = ({
  name,
  onNameChange,
  onEdit,
  onDelete,
  children,
  hoverForOnlyName,
  readonly,
  noHoverStyles,
  testid,
  className,
  labelFor,
}: InputRowProps) => {
  const { Colors } = useTheme();
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  const [nameHovered, setNameHovered] = useState(false);
  const [tempName, setTempName] = useState(name);

  useEffect(() => setTempName(name), [name]);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setTempName(e.target.value);
  const onApply = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // If the new name is valid, use it, otherwise reset the temp name back to the valid current name.
      return e.target.value ? onNameChange(e.target.value) : setTempName(name);
    },
    [name, onNameChange],
  );

  return (
    <Wrapper
      nameHovered={nameHovered}
      hoverForOnlyName={hoverForOnlyName}
      hasBlockAssignment={hasBlockAssignment}
      noHoverStyles={noHoverStyles}
      data-testid={testid}
      className={className}
    >
      <Row
        hasBlockAssignment={hasBlockAssignment}
        onMouseEnter={() => setNameHovered(true)}
        onMouseLeave={() => setNameHovered(false)}
      >
        {readonly ? (
          <Subtitle2>{name}</Subtitle2>
        ) : (
          <Label htmlFor={labelFor} className={cn('text-[14px]', labelFor && 'flex-grow cursor-pointer')}>
            <NameInput
              hasBlockAssignment={hasBlockAssignment}
              value={tempName}
              onChange={onChange}
              onBlur={onApply}
              minWidth={50}
              placeholder="Missing name"
            />
          </Label>
        )}
        {!readonly &&
          (hasBlockAssignment ? (
            <StudioButton
              color={Colors.DarkBlue}
              data-testid={compact([testid, name, 'edit']).join('-')}
              size="tiny"
              fontSize="10px"
              onClick={onEdit}
            >
              Edit
            </StudioButton>
          ) : (
            <div className="flex gap-[4px]">
              <StudioButton data-testid={compact([testid, name, 'edit']).join('-')} size="small" onClick={onEdit}>
                <Icon type="pen" />
              </StudioButton>
              <StudioButton
                data-testid={compact([testid, name, 'delete']).join('-')}
                size="small"
                onClick={onDelete}
                disabled={!onDelete}
              >
                <Icon type="trash" />
              </StudioButton>
            </div>
          ))}
      </Row>
      {children}
    </Wrapper>
  );
};

export const NameInput = styled(AutosizeInput)<{ hasBlockAssignment: boolean }>`
  margin: -2px;
  max-width: 200px;
  padding: 2px;
  background-color: transparent;
  border: none;
  text-overflow: ellipsis;
  :focus {
    background-color: ${GetColor.White};
  }
  :hover,
  :focus {
    outline: 1px solid ${({ hasBlockAssignment }) => (hasBlockAssignment ? GetColor.DarkBlue : GetColor.Primary.Main)};
    outline-offset: 0;
  }
`;

const Wrapper = styled.div<{
  nameHovered: boolean;
  hoverForOnlyName?: boolean;
  noHoverStyles?: boolean;
  hasBlockAssignment?: boolean;
}>`
  padding: ${({ hasBlockAssignment }) => (hasBlockAssignment ? '0' : '5px 15px 5px 15px')};
  width: 100%;

  ${({ nameHovered, hoverForOnlyName, noHoverStyles, hasBlockAssignment }) =>
    (!hoverForOnlyName || nameHovered) &&
    !noHoverStyles &&
    !hasBlockAssignment &&
    css`
      :hover {
        background-color: ${GetColor.GreyScale.Grey10};
      }
    `}
  > ul {
    margin-left: -25px;
    margin-right: -15px;
    padding: 0;
    min-height: 10px;
  }
`;

const Row = styled.div<{ hoverForOnlyName?: boolean; hasBlockAssignment?: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  height: ${({ hasBlockAssignment }) => (hasBlockAssignment ? '20px' : '30px')};
`;
